export const WALKTHROUGH_HIDDEN_KEY = 'rehearsal-director:walkthrough-hidden-v1';
export const WALKTHROUGH_SESSION_KEY = 'rehearsal-director:walkthrough-seen-v1';

function browserStorage(name) { try { return globalThis[name]; } catch { return null; } }

export function shouldOfferWalkthrough(view, identity, persistent, session) {
  if (view !== 'home' || identity) return false;
  if (persistent === undefined) persistent = browserStorage('localStorage');
  if (session === undefined) session = browserStorage('sessionStorage');
  try { if (persistent?.getItem(WALKTHROUGH_HIDDEN_KEY) === '1') return false; } catch {}
  try { if (session?.getItem(WALKTHROUGH_SESSION_KEY) === '1') return false; } catch {}
  return true;
}

export function rememberWalkthroughChoice(dontShowAgain, persistent, session) {
  if (persistent === undefined) persistent = browserStorage('localStorage');
  if (session === undefined) session = browserStorage('sessionStorage');
  try {
    if (dontShowAgain) persistent.setItem(WALKTHROUGH_HIDDEN_KEY, '1');
    else session.setItem(WALKTHROUGH_SESSION_KEY, '1');
    return '';
  } catch {
    return dontShowAgain
      ? 'Your “Don’t Show This Again” choice could not be saved on this device. The walkthrough may appear on your next visit.'
      : 'Your walkthrough choice could not be saved for this tab. It may appear again after a reload.';
  }
}

// Static illustrations in the tour reference existing controls; they never join,
// start, or alter a live room. All actor-private examples remain generic.
export const WALKTHROUGH_STEPS = Object.freeze([
  { title: 'How QLine Works', target: 'QLine home screen and example roles', visual: ['Director', 'Player + earpiece', 'Player + earpiece'], paragraphs: [
    'One person runs the game or rehearsal as the Director.',
    'Each player or actor uses their own phone and an earpiece. QLine quietly gives each person the directions and lines they need, while everyone else hears only the spoken performance.',
  ] },
  { title: 'Choose What You Want to Do', target: 'Home actions', visual: ['Play or Rehearse Together', 'Practice Alone', 'Join a Room'], paragraphs: [
    "If you're starting a group game, story, or rehearsal, choose Play or Rehearse Together.",
    'If you want to practice your own part, choose Practice Alone.',
    'And if someone else is directing, choose Join a Room.',
  ] },
  { title: 'Choose Your Scene', target: 'Library and script choices', visual: ['Choose from Library', 'QLine Library', 'My Library', 'Upload Script'], paragraphs: [
    'As the Director, start by choosing what you want to play or rehearse.',
    'You can choose a scene from the QLine Library, open one from My Library, or upload your own script.',
    'QLine can import PDF, DOCX, TXT, and Markdown files.',
    'Saved scenes can already have their audio ready. A new script may need QLine to prepare its voices the first time.',
  ] },
  { title: 'Check the Scene', target: 'Scene review', visual: ['Actors · length · characters', 'Private actor briefings', 'Looks Good — Continue'], paragraphs: [
    'Take a quick look at the scene.',
    'You can see how many actors it needs, about how long it takes, the characters, and any private actor briefings.',
    'If everything looks right, enter your name and tap Looks Good — Continue.',
  ] },
  { title: 'Invite Your Actors', target: 'Room code and QR code', visual: ['Room code', 'QR code', 'Join Room'], paragraphs: [
    'QLine now creates your rehearsal room.',
    'Each actor opens qlinethegame.com on their phone and joins using the QR code or room code.',
    'They enter their name and tap Join Room.',
  ] },
  { title: 'Assign the Roles', target: 'Connected cast', visual: ['Connected actors', 'Choose a character', 'Assign Randomly'], paragraphs: [
    'Once your actors are connected, assign each person a character.',
    'You can choose the roles yourself or use Assign Randomly.',
    'Each actor will only receive the private information meant for their character.',
  ] },
  { title: 'Get the Production Ready', target: 'Set Up Your Production', visual: ['Props and substitutes', 'Setting and staging', 'Optional costume', 'I Have the Setup'], paragraphs: [
    'Some scenes may give the actors a quick setup page before you begin.',
    'It can show the props you may want, an optional costume, where the scene takes place, and simple staging.',
    'Use what you have. You can substitute something or simply pretend an item is there.',
    'When you’re ready, tap I Have the Setup.',
  ] },
  { title: 'Know Your Character', target: 'Actor role screen', visual: ['Character', 'What the cast knows', 'Your objective', 'Your secret'], paragraphs: [
    'Each actor can see their character, what the cast knows, their objective, and any private information their character needs.',
    'Some information is meant only for that actor, so keep your own screen private.',
  ] },
  { title: 'Put In Your Earpiece', target: 'Play My Sound Check', visual: ['Private direction voice', 'Line voice', 'Yes, Sound Works'], paragraphs: [
    'Before the scene, put in your earpiece or earbuds.',
    'Then tap Play My Sound Check.',
    'You should hear two voices: one giving private directions, and another speaking your lines.',
    'If you hear both clearly, tap Yes, Sound Works.',
  ] },
  { title: 'Microphone Check', target: 'Automatic microphone check', visual: ['Microphone detection', 'Automatic timing'], paragraphs: [
    'Next, QLine checks your microphone automatically.',
    'If it works, QLine can tell when you finish your line and keep the scene moving.',
    "If it doesn't work, that's okay. QLine can use automatic timing instead.",
    "You don't have to fix anything just to rehearse.",
  ] },
  { title: 'Ready to Act', target: 'I’m Ready to Act', visual: ['Role ready', 'Sound ready', 'Setup ready', 'I’m Ready to Act'], paragraphs: [
    "When your role, sound, and setup are ready, tap I'm Ready to Act.",
    'QLine waits until the cast is ready before the scene begins.',
  ] },
  { title: 'Choose How Much Help You Want', target: 'Hear My Lines / Recall', visual: ['Hear My Lines', 'Recall'], paragraphs: [
    'The Director can choose how much help the actors get.',
    'With Hear My Lines, QLine privately speaks your line into your earpiece. You can say the line along with the voice.',
    "With Recall, your own lines stay silent. You'll still hear your private directions, but you'll say your lines from memory.",
    'Use Hear My Lines while you’re learning, then try Recall when you want to test yourself.',
  ] },
  { title: 'Start the Scene', target: 'START SCENE', visual: ['START SCENE', 'Private introductions', 'Automatic cues'], paragraphs: [
    'When everyone is ready, the Director taps START SCENE.',
    'From here, QLine takes care of the cues.',
    'Actors hear private introductions and directions through their own devices. When it’s your turn, listen, act, and say your line.',
    'QLine keeps the rehearsal moving from actor to actor.',
  ] },
  { title: 'If You Need Help During the Scene', target: 'Pause, replay, captions and speed', visual: ['Pause Everyone', 'Replay Prompt', 'Captions', 'My Voice Speed'], paragraphs: [
    'If you need more time, tap Pause Everyone.',
    'You can replay your private prompt, turn captions on or off, and change the speed of your private voice.',
    'The Director can also pause the scene, repeat the current cue, or use manual controls when needed.',
  ] },
  { title: 'Practice Alone', target: 'Practice Alone', visual: ['Choose a scene', 'Choose a character', 'Repeat · Back · Pause · Restart'], paragraphs: [
    "You don't need a whole cast to use QLine.",
    'Choose Practice Alone, pick a scene and your character, and work through your lines at your own pace.',
    'You can repeat lines, go back, pause, or restart the scene whenever you need.',
  ] },
  { title: 'When You’re Finished', target: 'Completed rehearsal and History', visual: ['Rehearsal summary', 'Director notes', 'No recording'], paragraphs: [
    'When the rehearsal ends, QLine can keep a summary of the rehearsal and the Director’s notes in History.',
    'QLine does not make a recording of your rehearsal.',
  ] },
]);
